import {
  createORPCClient,
  type InferClientOutputs,
  onFinish,
  onStart,
} from "@orpc/client";
import { RPCLink } from "@orpc/client/fetch";
import type { ContractRouterClient } from "@orpc/contract";
import type {
  GenericPaginated,
  ORPCClientContext,
  RouterContract,
} from "@tillix/api-contract";
import { getEnv } from "../env";
import { txLog } from "../utils/logger";
import { getORPCBaseUrl } from "./utils";

const c = (msg: string, bgColor?: string) => ({
  msg,
  tag: "oRPC",
  msgStyle: {
    background: bgColor ?? "#55F",
    "border-radius": "0.5em",
    padding: "0.2em 0.5em 0.1em 0.5em",
    color: "white",
  },
});

const link = new RPCLink<ORPCClientContext>({
  url: getORPCBaseUrl(),
  headers: ({ context }) => {
    const headers: Record<string, string> = {};
    if (context.disableCache) {
      headers["x-disable-cache"] = "true";
    }
    if (typeof document !== "undefined" && getEnv().env === "development") {
      const { tenant, tenantVariant } = document.documentElement.dataset;
      if (tenant) headers["x-tenant-id"] = tenant;
      if (tenantVariant) headers["x-variant-id"] = tenantVariant;
    }
    return headers;
  },
  interceptors:
    getEnv().env === "development"
      ? [
          onStart(async ({ input, path }) => {
            txLog(c(`🌐 ${path?.join?.("/")}`), {
              input,
            });
          }),
        ]
      : [],
  adapterInterceptors:
    getEnv().env === "development"
      ? [
          onFinish(async ([err, output], { next }) => {
            if (err) {
              console.error("Error in oRPC call:", err);
            }
            if (output) {
              const success = output.status < 400;
              const { json } = await output
                .clone()
                .json()
                .catch(() => null);

              txLog(
                c(
                  `${success ? "🟢" : "🔴"} ${output.url.split(`${getORPCBaseUrl()}/`)[1]}`,
                  success ? "#20AF20" : "#F43326",
                ),
                { [success ? "output" : "error"]: json },
              );
            }
          }),
        ]
      : [],
});

export type Api = ContractRouterClient<RouterContract, ORPCClientContext>;
export const api: Api = createORPCClient<Api>(link);
export type ApiOutputs = InferClientOutputs<typeof api>;

export type ResourceApi<T> = {
  get: (...args: any[]) => Promise<GenericPaginated<T>>;
  put?: (...args: any[]) => Promise<unknown>;
  post?: (...args: any[]) => Promise<unknown>;
  delete?: (...args: any[]) => Promise<unknown>;
  checkStale?: (...args: any[]) => Promise<{ staleIds: string[] }>;
};

declare global {
  interface Window {
    __tilliX_orpc_api__: () => Api;
  }
}

if (typeof window !== "undefined" && getEnv().env === "development") {
  window.__tilliX_orpc_api__ = () => api;
}
